import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
// next
import { useRouter } from 'next/router';
// @mui
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  Fab,
  Button,
  Tab,
  Tabs,
  Tooltip,
  Dialog,
} from '@mui/material';
import { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../hooks/useAuth';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormProvider,
  RHFTextField,
  RHFFileUploadField,
} from '../../components/hook-form';
// components
import DIOText from '../../components/DIOText';
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import Label from '../../components/Label';
// swr
import useSWR from 'swr';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
import { fKrKSTFile } from '../../utils/formatTime';
import axios from '../../utils/axios';
import {default as axiosNormal} from 'axios';


const NearTooltip = styled(({ theme, className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  zIndex: 100,
  width: 200,
  [`& .${tooltipClasses.tooltip}`]: {
    marginRight: theme.spacing(0),
    ...theme.typography.caption,
  },
}));

// ----------------------------------------------------------------------

const ScrollContent = styled('div')(({ theme }) => ({
  maxHeight: 'calc(100vh - 324px)',
  overflow: 'auto',
}));

export default function MessageDialog({ companyId, companyName }) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const router = useRouter();
  const { oid } = router.query;
  const { data, isValidating, mutate } = useSWR((user && oid) ? `/api/crews/offers/${oid}/match-files/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const [currentTab, setCurrentTab] = useState('received');
  const [status, setStatus] = useState('list');

  // send
  const [selectedSendDetail, setSelectedSendDetail] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [noContent, setNoContent] = useState(true);
  const [contentLength, setContentLength] = useState(0);

  const UploadFileSchema = Yup.object().shape({
    content: Yup.string().max(1000, '1,000자 이내로 작성해주세요.'),
    file: Yup.mixed(),
  });

  const defaultValues = {
    content: '',
    file: {name: '', file: ''},
  };

  const methods = useForm({
    resolver: yupResolver(UploadFileSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
  } = methods;

  const watchValues = async (value, name, type) => {
    setNoContent(!!!value.content && !!!value.file.name);
    setContentLength(value.content?.trim().length);
  };

  useEffect(() => {
    const subscription = watch((value, { name, type }) => watchValues(value, name, type ));
    return () => subscription.unsubscribe();
  }, [watch]);

  // receive
  const [selectedReceiveDetail, setSelectedReceiveDetail] = useState({});

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setTooltipOpen(false);
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
    goList();
    setCurrentTab('received');
  };

  const goList = () => {
    setStatus('list');
    reset();
    setNoContent(true);
  };

  const goPreSend = () => {
    setStatus('preSend');
  };

  const goSend = () => {
    setStatus('send');
  };

  const goSendDetail = (s) => {
    setStatus('sendDetail');
    setSelectedSendDetail(s);
  };

  const goReceiveDetail = async (r) => {
    setStatus('receiveDetail');
    setSelectedReceiveDetail(r);
    if (!r.read_at) {
      await axios.post(`/api/crews/offers/${oid}/company-match-files/${r.id}/read/`);
      mutate();
    }
  };

  const openReceiveFile = async (item) => {
    const response = await axios.post(`/api/crews/offers/${oid}/company-match-files/${item.id}/url/`);
    window.open(response.data.url, '_blank', 'noopener, noreferrer');
  };

  const openSentFile = async (item) => {
    const response = await axios.post(`/api/crews/offers/${oid}/match-files/${item.id}/url/`);
    window.open(response.data.url, '_blank', 'noopener, noreferrer');
  };

  const fileUploadBeforeSubmit = async (f) => {
    if (!f.file && !f.name) return {file: null, name: null};
    const urlResponse = await axios.post(
      `/api/crews/offers/${oid}/match-files/upload-url/`,
      {filename: f.name}
    );
    const {filename, url} = urlResponse.data;

    // upload file to s3 via presigned url
    await axiosNormal.put(
      url,
      f,
      {
        headers: {
          'Content-Type': f.type
        }
      }
    );
    return { file: filename, name: f.name };
  };

  const sendToCompany = async (data) => {
    try {
      setIsSubmit(true);
      const fileData = await fileUploadBeforeSubmit(data.file);
      await axios.post(`api/crews/offers/${oid}/match-files/`, {  
        content: data.content,
        file: fileData.file,
        name: fileData.name,
      });
      enqueueSnackbar('메시지를 보냈습니다');
      mutate();
      setIsSubmit(false);
      goList();
      setCurrentTab('sent');
    } catch (err) {
      setIsSubmit(false);
      console.error(err);
    }
  };

  // tooltip
  const [tooltipOpen, setTooltipOpen] = useState(true);

  return (
    <>
    <NearTooltip
      title={
        data?.unread
        ? '회사에서 보낸 메시지를 확인해주세요. 답변이 필요하다면 회사에 메시지를 보내세요.'
        : `${companyName}에 질문이나 전달할 메시지, 자료가 있다면 여기를 선택해주세요.`
      }
      arrow
      placement="right"
      open={tooltipOpen}
      disableInteractive
      disableTouchListener
      disableHoverListener
      disableFocusListener
      leaveTouchDelay={0}
    >
      <Badge
        color="secondary"
        variant="standard"
        badgeContent={data?.unread}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        overlap="circular"
      >
        <Fab
          disableRipple
          variant="extended"
          size="medium"
          sx={{
            color: "primary.main",
            backgroundColor: "common.white",
            borderColor: "primary.main",
            border: '1px solid black',
          }}
          onClick={handleOpen}
        >
          <Iconify
            icon={open ? "eva:close-fill" : "eva:email-fill"}
            sx={{ width: 24, height: 24, color: "primary.main", ...open && { color: "text.secondary" } }}
          />
          메시지
        </Fab>
      </Badge>
    </NearTooltip>

      <Dialog
        fullScreen
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          '& .MuiMenuItem-root': {
            typography: 'body2',
          },
        }}
      >
        {status === 'list' && (
        <>
        <Stack sx={{ position: 'fixed', width: '100%', zIndex: 10 }}>
          <Stack sx={{ p: 3 }}>
            <Box sx={{ justifyContent: "end", display: "flex" }}>
              <IconButton edge="end" color="inherit" onClick={handleClose}>
                <Iconify icon="eva:close-fill"/>
              </IconButton>
            </Box>
            <Stack direction="row" spacing={1}>
              <Avatar
                src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/bji_${companyId%4 + 1}.png`}
                color={'default'}
                sx={{
                  width: 24,
                  height: 24,
                }}
              />
              <Typography variant="h6">
                {companyName}에
              </Typography>
            </Stack>
            <Typography variant="h6">
              질문이나 전달할 메시지, 자료가 있나요?
            </Typography>
            <Typography sx={{ mt: 0.5, wordBreak: "keep-all" }}>
              회사에 질문, 계정 정보 전달, 추가 자료 전달이 필요한 경우 메시지를 활용하세요.
            </Typography>
            <Button fullWidth variant="contained" sx={{ mt: 3 }} size="large" onClick={goPreSend}>
              회사에 메시지 보내기
            </Button>
          </Stack>

          {/* tab */}
          <Box sx={{ backgroundColor: "grey.200", px: 3, }}>
            <Tabs
              allowScrollButtonsMobile
              variant="scrollable"
              scrollButtons="auto"
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
            >
              <Tab
                disableRipple key="received" value={"received"}
                label={
                  <Stack spacing={1} direction="row" alignItems="center">
                    <div>받은 메시지</div>
                    {data?.unread > 0 && (<Label sx={{ opacity: currentTab === "received" ? 1 : 0.5 }} variant="filled" color="secondary">{data?.unread}</Label>)}
                  </Stack>
                }
              />
              <Tab
                disableRipple key="sent" value={"sent"}
                label={
                  <Stack spacing={1} direction="row" alignItems="center">
                    <div>보낸 메시지</div>
                    {data?.sent?.length > 0 && (<Label sx={{ opacity: currentTab === "sent" ? 1 : 0.5 }} variant="ghost" color="default">{data?.sent?.length}</Label>)}
                  </Stack>
                }
              />
            </Tabs>

          </Box>
        </Stack>

        <Box sx={{ mt: '324px' }} />

        {isValidating && (
          <Stack spacing={2} sx={{ py: 2 }}>
            <Skeleton variant="rectangulur" height={50} />
            <Skeleton variant="rectangulur" height={50} />
            <Skeleton variant="rectangulur" height={50} />
          </Stack>
        )}
        {/* received list */}
        {!isValidating && currentTab === "received" && data?.received?.length === 0 && (
          <Stack sx={{ py: 15 }}>
            <Typography sx={{ textAlign: 'center', color: "text.secondary" }} variant="subtitle1">
              아직 받은 메시지가 없어요.
            </Typography>
            <Typography sx={{ textAlign: 'center', color: "text.secondary" }} variant="body2">
              메시지가 오면 카카오톡으로 알려드릴게요.
            </Typography>
          </Stack>
        )}
        {!isValidating && currentTab === "received" && data?.received?.length > 0 && (
          <ScrollContent>
            {data?.received?.map((r, index) => (
              <Stack
                key={`r-${index}`}
                sx={{
                  px: 2.5, py: 2, width: 1,
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: "grey.300",
                  },
                  '&:active': {
                    backgroundColor: "grey.400",
                  }
                }}
                onClick={() => goReceiveDetail(r)}
              >
                <Typography variant="subtitle1" sx={{ color: r.read_at ? "text.disabled" : "text.primary", overflow: "hidden", textOverflow: "ellipsis" }} noWrap>
                  {r.content || r.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {fKrKSTFile(r.created_at)}
                </Typography>
              </Stack>
            ))}
          </ScrollContent>
        )}

        {/* sent list */}
        {!isValidating && currentTab === "sent" && data?.sent?.length === 0 && (
          <Stack sx={{ py: 15 }}>
            <Typography sx={{ textAlign: 'center', color: "text.secondary" }} variant="subtitle1">
              보낸 메시지가 없어요.
            </Typography>
            <Typography sx={{ textAlign: 'center', color: "text.secondary" }} variant="body2">
              회사에 메시지를 보내보세요.
            </Typography>
          </Stack>
        )}
        {!isValidating && currentTab === "sent" && data?.sent?.length > 0 && (
          <ScrollContent>
            {data?.sent?.map((s, index) => (
              <Stack
                key={`s-${index}`}
                sx={{
                  px: 2.5, py: 2, width: 1,
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: "grey.300",
                  },
                  '&:active': {
                    backgroundColor: "grey.400",
                  }
                }}
                onClick={() => goSendDetail(s)}
              >
                <Typography variant="subtitle1" sx={{ overflow: "hidden", textOverflow: "ellipsis" }} noWrap>
                  {s.content || s.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {fKrKSTFile(s.created_at)}
                  </Typography>
                  {s.read_at && (
                    <Iconify icon="eva:checkmark-fill" sx={{ width: 16, height: 16, color: "text.disabled" }} />
                  )}
                </Stack>
              </Stack>
            ))}
          </ScrollContent>
        )}

        </>
        )}
        {status === 'preSend' && (
        <Box
          sx={{ p: 3 }}
        >
          <Box sx={{ justifyContent: "space-between", display: "flex", mb: 1 }}>
            <IconButton edge="start" color="inherit" onClick={goList}>
              <Iconify icon="ic:chevron-left"/>
            </IconButton>
            <IconButton edge="end" color="inherit" onClick={handleClose}>
              <Iconify icon="eva:close-fill"/>
            </IconButton>
          </Box>
          <Typography variant="h5" gutterBottom>
            회사에 메시지는 이렇게 보내세요.
          </Typography>
          <Typography variant="body1" paragraph>
            보내는 메시지는 전부 <DIOText/>에서 검수 후 회사에 전달합니다.
          </Typography>
          <Box
            sx={{ p: 3, backgroundColor: 'grey.200', borderRadius: 2 }}
          >
            <Stack spacing={.5}>
              <Typography variant="h6">
                ✉️ 이럴 때 메시지를 보내세요!
              </Typography>
              <Typography variant="body1" sx={{ pl: 2 }}>
                • 회사에 질문이 있을 때
              </Typography>
              <Typography variant="body1" sx={{ pl: 2 }}>
                • 추가적인 전달 사항이 있을 때
              </Typography>
              <Typography variant="body1" sx={{ pl: 2 }}>
                • 추가적인 자료 전달이 필요할 때
              </Typography>
            </Stack>
            
            <Stack spacing={.5} sx={{ mt: 3 }}>
              <Typography variant="h6">
                ❌ 이런 메시지는 보내지 마세요.
              </Typography>
              <Typography variant="body2" sx={{ pb: 0.5 }}>
                <DIOText/>에서 메시지 내용을 검수 후 메시지를 전달하지 않을 수 있습니다. 
              </Typography>
              <Typography variant="body1" sx={{ pl: 2 }}>
                • 신상을 유추할 수 있는 내용
              </Typography>
              <Typography variant="body1" sx={{ pl: 2 }}>
                • <DIOText />를 통하지 않는<br/>&nbsp;&nbsp; 회사와의 직접 계약 관련 내용
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" justifyContent="flex-end">
            <Button sx={{ mt: 4, px: 3 }} fullWidth variant="contained" size="large" onClick={goSend}>
              확인했어요
            </Button>
          </Stack>
        </Box>
        )}
        {status === 'send' && (
        <Box
          sx={{ p: 3 }}
        >
          <Box sx={{ justifyContent: "space-between", display: "flex", mb: 1 }}>
            <IconButton edge="start" color="inherit" onClick={goPreSend}>
              <Iconify icon="ic:chevron-left"/>
            </IconButton>
            <IconButton edge="end" color="inherit" onClick={handleClose}>
              <Iconify icon="eva:close-fill"/>
            </IconButton>
          </Box>
          <Typography variant="h5" gutterBottom>
            {companyName}에 메시지를 보내세요.
          </Typography>
          <Typography variant="body1" paragraph>
            내용을 작성하거나 파일을 업로드 해주세요.<br/>
            회사에 전달해드릴게요.
          </Typography>

          <FormProvider methods={methods}>
            <RHFTextField
              name="content"
              label={"전달 사항"}
              fullWidth
              multiline
              minRows={5}
              maxRows={5}
              placeholder="계정 정보 등 텍스트로 전달할 내용이 있다면 적어주세요"
            />
            <Box sx={{ width: 1, textAlign: "end" }}>
              <Typography variant="caption" sx={{ color: (contentLength > 1000) ? "error.main" : "text.secondary" }}>
                {contentLength === 0 && "1,000자 미만"}
                {contentLength > 0 && `${contentLength?.toLocaleString()}자/1,000자 미만`}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} component="div" role="presentation">
              <Typography variant="caption" sx={{ color: "grey.500"}}>또는</Typography>
            </Divider>

            <RHFFileUploadField
              link
              name="file"
              label="자료 업로드"
              placeholder={"파일을 업로드 해주세요"}
            />
          </FormProvider>

          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 6 }}>
            <LoadingButton
              onClick={goList}
              size="large"
              sx={{ px: 4 }}
              loading={isSubmit}
            >
              취소
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={handleSubmit(sendToCompany)}
              size="large"
              sx={{ px: 6 }}
              loading={isSubmit}
              disabled={noContent}
            >
              보내기
            </LoadingButton>
          </Stack>
        </Box>
        )}
        {status === 'sendDetail' && (
          <Box
            sx={{ p: 3 }}
          >
            <Box sx={{ justifyContent: "space-between", display: "flex", mb: 1 }}>
              <IconButton edge="start" color="inherit" onClick={goList}>
                <Iconify icon="ic:chevron-left"/>
              </IconButton>
              <IconButton edge="end" color="inherit" onClick={handleClose}>
                <Iconify icon="eva:close-fill"/>
              </IconButton>
            </Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              내가 {companyName}에 보낸 메시지에요.
            </Typography>

            <Box
              sx={{ p: 2, backgroundColor: 'grey.200', borderRadius: 2, height: 'calc(100vh - 220px)', overflow: 'auto' }}
            >
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SvgIconStyle src={`/icons/icon-pencil-mono.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="subtitle2">
                    작성한 전달 사항
                  </Typography>
                </Stack>
                <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', pl: 3 }} variant="body2">
                  {selectedSendDetail.content || '-'}
                </Typography>
              </Stack>

              {selectedSendDetail.name && (
              <Stack sx={{ mt: 3 }} spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SvgIconStyle src={`/icons/icon-document-mono.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="subtitle2">
                    업로드한 파일
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 3, color: "text.secondary" }}>
                  <Button
                    sx={{ color: "text.secondary", textTransform: 'none'}}
                    startIcon={<Iconify icon="material-symbols:download-rounded" />}
                    onClick={() => openSentFile(selectedSendDetail)}
                  >
                    {selectedSendDetail.name}
                  </Button>
                </Stack>
              </Stack>
              )}
            </Box>
            <Stack
              sx={{
                width: 1, p: 3,
                position: "absolute", bottom: 0, right: 0,
              }}
            >
              <Button fullWidth variant="contained" size="large" onClick={goList}>
                확인했어요
              </Button>
            </Stack>
          </Box>
        )}
        {status === 'receiveDetail' && (
          <Box
            sx={{ p: 3 }}
          >
            <Box sx={{ justifyContent: "space-between", display: "flex", mb: 1 }}>
              <IconButton edge="start" color="inherit" onClick={goList}>
                <Iconify icon="ic:chevron-left"/>
              </IconButton>
              <IconButton edge="end" color="inherit" onClick={handleClose}>
                <Iconify icon="eva:close-fill"/>
              </IconButton>
            </Box>
            <Typography variant="subtitle1" gutterBottom>
              회사가 메시지를 보냈어요.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2 }}>
              메시지 내용을 확인하고, 답장이 필요하다면 회사에 다시 메시지를 보내세요.
            </Typography>

            <Box
              sx={{ p: 2, backgroundColor: 'grey.200', borderRadius: 2, height: 'calc(100vh - 270px)', overflow: 'auto' }}
            >
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SvgIconStyle src={`/icons/icon-pencil-mono.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="subtitle2">
                    전달 사항
                  </Typography>
                </Stack>
                <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', pl: 3 }} variant="body2">
                  {selectedReceiveDetail.content || '-'}
                </Typography>
              </Stack>

              {selectedReceiveDetail.name && (
              <Stack sx={{ mt: 3 }} spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SvgIconStyle src={`/icons/icon-document-mono.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="subtitle2">
                    업로드한 파일
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 3, color: "text.secondary" }}>
                  <Button
                    sx={{ color: "text.secondary", textTransform: 'none'}}
                    startIcon={<Iconify icon="material-symbols:download-rounded" />}
                    onClick={() => openReceiveFile(selectedReceiveDetail)}
                  >
                    {selectedReceiveDetail.name}
                  </Button>
                </Stack>
              </Stack>
              )}
            </Box>
            <Stack
              sx={{
                width: 1, p: 3,
                position: "absolute", bottom: 0, right: 0,
              }}
            >
              <Button fullWidth variant="contained" size="large" onClick={goList}>
                확인했어요
              </Button>
            </Stack>
          </Box>
        )}
      </Dialog>
    </>
  );
}
